import {
  makeObservable,
  observable,
  action,
  computed,
  runInAction,
  toJS,
} from "mobx";
import localforage from "localforage";
import ServiceStore from "./ServiceStore";
import { ConflictResolver } from "./offline/ConflictResolver";
import type RootStore from "./RootStore";
import type {
  ResolveEntity,
  ResolveData,
  ResolvePatch,
  ResolveQuery,
} from "../types/ServiceStoreTypeMap";

export interface OfflineMetadata {
  _isTemp?: boolean;
  _localCreatedAt?: number;
  _localUpdatedAt?: number;
  _localDeletedAt?: number;
  _deleted?: boolean;
  _syncStatus?: "pending" | "syncing" | "synced" | "error";
  _conflictData?: unknown;
}

export interface OfflineServiceStoreOptions {
  enableOffline?: boolean;
  conflictStrategy?: "client" | "server" | "merge" | "manual";
  cacheTTL?: number;
  syncOnReconnect?: boolean;
}

/**
 * OfflineServiceStore extends ServiceStore with offline-first capabilities
 *
 * @template S - The service path string (e.g., 'customers', 'products')
 */
export class OfflineServiceStore<
  S extends string = string
> extends ServiceStore<S> {
  offlineStorage: LocalForage | null = null;
  lastSyncTime: Date | null = null;
  syncInProgress = false;
  conflictResolver: ConflictResolver<any>;
  offlineOptions: OfflineServiceStoreOptions = {
    enableOffline: true,
    conflictStrategy: "server",
    cacheTTL: 5 * 60 * 1000, // 5 minutes
    syncOnReconnect: true,
  };

  private syncPromise: Promise<void> | null = null;
  private pendingSync = new Set<string>();

  constructor(
    rootStore: RootStore,
    serviceName: S,
    options: {
      offline?: OfflineServiceStoreOptions;
      [key: string]: unknown;
    } = {}
  ) {
    // Pass options to parent, extracting standard ServiceStore options
    super(rootStore, serviceName, {
      apiVersion: options.apiVersion as number | undefined,
      channels: options.channels as boolean | undefined,
      debugMode: options.debugMode as boolean | undefined,
      enableErrorNotifications: options.enableErrorNotifications as
        | boolean
        | undefined,
      disableCreatedListener: options.disableCreatedListener as
        | boolean
        | undefined,
      disableUpdatedListener: options.disableUpdatedListener as
        | boolean
        | undefined,
      disablePatchedListener: options.disablePatchedListener as
        | boolean
        | undefined,
      disableRemovedListener: options.disableRemovedListener as
        | boolean
        | undefined,
      disableStatusListener: options.disableStatusListener as
        | boolean
        | undefined,
      enableEntityOptimization: options.enableEntityOptimization as
        | boolean
        | undefined,
      pagination: options.pagination as number | undefined,
      enableStorage: options.enableStorage as boolean | undefined,
      idField: options.idField as string | undefined,
    });

    Object.assign(this.offlineOptions, options.offline || {});

    if (this.offlineOptions.enableOffline) {
      // Use the same database name as ServiceStore for consistency
      // This consolidates all storage into a single IndexedDB database
      this.offlineStorage = localforage.createInstance({
        name: this.rootStore.appName,
        storeName: `offline-${this.name || serviceName}`,
      });

      // Share storage reference with parent class so addOrUpdateEvent
      // (called by socket events) writes to the same storage
      this.storage = this.offlineStorage;

      this.conflictResolver = new ConflictResolver(this.name || serviceName);

      // Use makeObservable with explicit annotations for subclass properties
      makeObservable(this, {
        // Offline-specific observables
        lastSyncTime: observable,
        syncInProgress: observable,
        // Offline-specific computed
        hasPendingSync: computed,
        // Offline-specific actions
        syncWithServer: action,
      });

      this.initializeOfflineStore();
    }
  }

  private async initializeOfflineStore() {
    // Load cached data first
    await this.hydrateFromOfflineStorage();

    // Setup connection listener for auto-sync
    if (this.offlineOptions.syncOnReconnect && this.rootStore.connectionStore) {
      this.setupConnectionListener();
    }
  }

  private async hydrateFromOfflineStorage() {
    if (!this.offlineStorage) return;

    try {
      const keys = await this.offlineStorage.keys();
      const items = await Promise.all(
        keys.map(async (key) => {
          if (key.startsWith("_")) return null; // Skip metadata keys
          return await this.offlineStorage!.getItem<any>(key);
        })
      );

      runInAction(() => {
        items.forEach((item) => {
          if (item && !item._deleted) {
            const existingIndex = this.items.findIndex(
              (i: any) => i[this.idField] === item[this.idField]
            );
            if (existingIndex >= 0) {
              this.items[existingIndex] = item;
            } else {
              this.items.push(item);
            }
          }
        });
      });
    } catch (error) {
      console.error(
        `Failed to hydrate ${this.name} from offline storage:`,
        error
      );
    }
  }

  private setupConnectionListener() {
    if (!this.rootStore.connectionStore) return;

    // Listen for connection changes
    const connectionStore = this.rootStore.connectionStore;
    if (connectionStore.addConnectionListener) {
      connectionStore.addConnectionListener(async (isOnline: boolean) => {
        if (isOnline && this.offlineOptions.syncOnReconnect) {
          await this.syncWithServer();
        }
      });
    }
  }

  find = async (
    params: {
      query?: Partial<ResolveQuery<S>> & { [key: string]: unknown };
    } = {}
  ): Promise<{
    data: ResolveEntity<S>[];
    total: number;
    result?: { data: ResolveEntity<S>[] };
  } | null> => {
    const socketConnected = this.rootStore.app?.io?.connected ?? false;
    const isOffline =
      !navigator.onLine ||
      (this.rootStore.connectionStore?.isOffline ?? false) ||
      !socketConnected;

    if (isOffline && this.offlineOptions.enableOffline) {
      return this.findOffline(params);
    }

    try {
      // Directly call the service since we can't call super on arrow functions
      runInAction(() => {
        this.isFindPending = true;
      });
      let response: { data: ResolveEntity<S>[]; total: number } | null = null;

      try {
        if (this.pagination.enabled) {
          if (!params.query)
            Object.assign(params, {
              query: {},
            });
          if (params.query) {
            Object.assign(params.query, {
              $limit: this.pagination.pageSize,
              $skip: this.pagination.pageSize * this.pagination.currentPage,
            });
          }
        }

        const serviceResponse = await this.service.find(params);

        // Handle paginated response
        if (serviceResponse?.data && serviceResponse?.total !== undefined) {
          response = {
            data: serviceResponse.data as ResolveEntity<S>[],
            total: serviceResponse.total,
          };
          runInAction(() => {
            this.items = response!.data as ResolveEntity<S>[];
            this.setPageTotal(response!.total);
          });
        } else if (Array.isArray(serviceResponse)) {
          response = {
            data: serviceResponse as ResolveEntity<S>[],
            total: serviceResponse.length,
          };
          runInAction(() => {
            this.items = response!.data as ResolveEntity<S>[];
          });
        }

        runInAction(() => {
          this.errorOnFind = null;
        });
      } catch (error: any) {
        runInAction(() => {
          this.errorOnFind = error;
        });
        throw error;
      } finally {
        runInAction(() => {
          this.isFindPending = false;
        });
      }

      // Cache the results
      if (this.offlineOptions.enableOffline) {
        await this.cacheResults(response);
      }

      return response;
    } catch (error) {
      // Fallback to offline mode
      if (this.offlineOptions.enableOffline) {
        console.warn(`${this.name}: Falling back to offline mode`, error);
        return this.findOffline(params);
      }
      throw error;
    }
  };

  private async findOffline(params?: {
    query?: Partial<ResolveQuery<S>> & { [key: string]: unknown };
  }): Promise<{ data: ResolveEntity<S>[]; total: number }> {
    const items = this.items.filter(
      (item) => !(item as OfflineMetadata)._deleted
    );

    // Apply basic query filters if provided
    let filtered = items;
    if (params?.query) {
      filtered = this.applyOfflineQuery(items, params.query);
    }

    // Paginate results
    const skip = (params?.query?.$skip as number) || 0;
    const limit = (params?.query?.$limit as number) || 10;
    const dataWithIds = filtered.slice(skip, skip + limit);

    return {
      total: filtered.length,
      data: dataWithIds,
    };
  }

  private applyOfflineQuery(
    items: ResolveEntity<S>[],
    query: Record<string, unknown>
  ): ResolveEntity<S>[] {
    let result = [...items];

    // Apply filters
    Object.entries(query).forEach(([key, value]) => {
      if (key.startsWith("$")) return; // Skip operators for now

      result = result.filter((item) => {
        const itemValue = (item as Record<string, unknown>)[key];

        if (typeof value === "object" && value !== null) {
          // Handle operators
          const valueObj = value as Record<string, unknown>;
          if ("$in" in valueObj && Array.isArray(valueObj.$in)) {
            return valueObj.$in.includes(itemValue);
          }
          if ("$ne" in valueObj) {
            return itemValue !== valueObj.$ne;
          }
          // Add more operators as needed
        }

        return itemValue === value;
      });
    });

    // Apply sorting
    if (query.$sort && typeof query.$sort === "object") {
      const sortKeys = Object.entries(query.$sort as Record<string, number>);
      result.sort((a, b) => {
        for (const [key, direction] of sortKeys) {
          const aVal = (a as Record<string, unknown>)[key];
          const bVal = (b as Record<string, unknown>)[key];

          if (aVal !== undefined && bVal !== undefined) {
            if (aVal < bVal) return direction === 1 ? -1 : 1;
            if (aVal > bVal) return direction === 1 ? 1 : -1;
          }
        }
        return 0;
      });
    }

    return result;
  }

  get = async (
    id: number | string,
    params: { query?: Partial<ResolveQuery<S>> } = {}
  ): Promise<ResolveEntity<S> | undefined> => {
    // Temp IDs should NEVER be sent to server - always return from cache
    if (String(id).startsWith("temp_")) {
      const cached = this.items.find(
        (item) =>
          String((item as Record<string, unknown>)[this.idField]) ===
            String(id) && !(item as OfflineMetadata)._deleted
      );
      if (cached) {
        console.log(`[${this.name}] get() returning cached temp item: ${id}`);
        return cached;
      }
      // Temp item not found - may have been synced, return undefined
      console.warn(`[${this.name}] get() temp item not found in cache: ${id}`);
      return undefined;
    }

    const socketConnected = this.rootStore.app?.io?.connected ?? false;
    const isOffline =
      !navigator.onLine ||
      (this.rootStore.connectionStore?.isOffline ?? false) ||
      !socketConnected;

    // Try local first if offline
    if (isOffline && this.offlineOptions.enableOffline) {
      const cached = this.items.find(
        (item) =>
          String((item as Record<string, unknown>)[this.idField]) ===
            String(id) && !(item as OfflineMetadata)._deleted
      );
      if (cached) {
        return cached;
      }
    }

    try {
      // Directly call the service since we can't call super on arrow functions
      runInAction(() => {
        this.isGetPending = true;
      });
      let item: ResolveEntity<S> | null = null;

      try {
        item = await this.service.get(id, params);

        const index = this.items.findIndex(
          (i) =>
            String((i as Record<string, unknown>)[this.idField]) === String(id)
        );
        if (index >= 0) {
          runInAction(() => {
            this.items[index] = item as ResolveEntity<S>;
          });
        } else {
          runInAction(() => {
            this.items.push(item as ResolveEntity<S>);
          });
        }

        runInAction(() => {
          this.errorOnGet = null;
        });
      } catch (error: unknown) {
        runInAction(() => {
          this.errorOnGet = error as { name: string; message: string };
        });
        throw error;
      } finally {
        runInAction(() => {
          this.isGetPending = false;
        });
      }

      // Cache the result
      if (this.offlineOptions.enableOffline && this.offlineStorage && item) {
        await this.offlineStorage.setItem(String(id), item);
      }

      return item ?? undefined;
    } catch (error) {
      // Return cached version if available
      if (this.offlineOptions.enableOffline) {
        const cached = this.items.find(
          (item) =>
            String((item as Record<string, unknown>)[this.idField]) ===
              String(id) && !(item as OfflineMetadata)._deleted
        );
        if (cached) {
          return cached;
        }
      }
      throw error;
    }
  };

  create = async (
    data: ResolveData<S>,
    params: object = {}
  ): Promise<ResolveEntity<S> | undefined> => {
    // Check multiple signals for offline status:
    // 1. navigator.onLine - network interface status
    // 2. connectionStore.isOffline - app-level offline tracking
    // 3. socket.connected - actual socket connection status (most reliable)
    const socketConnected = this.rootStore.app?.io?.connected ?? false;
    const isOffline =
      !navigator.onLine ||
      (this.rootStore.connectionStore?.isOffline ?? false) ||
      !socketConnected;

    console.log(
      `[${this.name}] create() called - isOffline=${isOffline}, navigator.onLine=${navigator.onLine}, connectionStore.isOffline=${this.rootStore.connectionStore?.isOffline}, socket.connected=${socketConnected}, enableOffline=${this.offlineOptions.enableOffline}`
    );

    if (isOffline && this.offlineOptions.enableOffline) {
      console.log(`[${this.name}] Routing to createOffline`);
      return this.createOffline(data);
    }

    try {
      // Directly call the service
      runInAction(() => {
        this.isCreatePending = true;
      });
      let item: ResolveEntity<S> | null = null;

      try {
        item = await this.service.create(data, params);

        runInAction(() => {
          // Use addOrUpdateEvent to prevent duplicates from socket.io "created" events
          // The socket event may arrive before this promise resolves, causing duplicates
          // if we used push() directly
          this.addOrUpdateEvent(item as Record<string, unknown>, false);
          this.errorOnCreate = null;
        });
      } catch (error: unknown) {
        runInAction(() => {
          this.errorOnCreate = error as { name: string; message: string };
        });
        throw error;
      } finally {
        runInAction(() => {
          this.isCreatePending = false;
        });
      }

      // Cache the result
      if (this.offlineOptions.enableOffline && this.offlineStorage && item) {
        const itemId = (item as Record<string, unknown>)[this.idField];
        await this.offlineStorage.setItem(String(itemId), item);
      }

      return item ?? undefined;
    } catch (error) {
      if (this.offlineOptions.enableOffline) {
        console.warn(`${this.name}: Creating offline`, error);
        return this.createOffline(data);
      }
      throw error;
    }
  };

  private async createOffline(data: ResolveData<S>): Promise<ResolveEntity<S>> {
    console.log(`[${this.name}] createOffline called`);

    // Generate temporary ID
    const tempId = `temp_${Date.now()}_${Math.random()}`;
    const item = {
      ...data,
      [this.idField]: tempId,
      _isTemp: true,
      _localCreatedAt: Date.now(),
      _syncStatus: "pending" as const,
    } as unknown as ResolveEntity<S>;

    // Store locally
    if (this.offlineStorage) {
      try {
        await this.offlineStorage.setItem(tempId, item);
        console.log(`[${this.name}] Stored offline item: ${tempId}`);
      } catch (error) {
        console.error(`[${this.name}] Failed to store offline item:`, error);
      }
    } else {
      console.error(
        `[${this.name}] offlineStorage is NULL - cannot store item!`
      );
    }

    runInAction(() => {
      // Use addOrUpdateEvent for consistency (even for offline items)
      this.addOrUpdateEvent(item as Record<string, unknown>, false);
    });

    // Queue for sync
    if (this.rootStore.offlineQueueManager) {
      try {
        await this.rootStore.offlineQueueManager.enqueue({
          storeName: this.name || "",
          method: "create",
          data: toJS(data),
          conflictResolution: this.offlineOptions.conflictStrategy,
        });
        console.log(`[${this.name}] Queued offline create operation`);
      } catch (error) {
        console.error(
          `[${this.name}] Failed to queue offline operation:`,
          error
        );
      }
    } else {
      console.error(
        `[${this.name}] offlineQueueManager is NULL - cannot queue operation!`
      );
    }

    this.pendingSync.add(tempId);

    return item;
  }

  patch = async (
    id: number | string | null,
    data: Partial<ResolvePatch<S>>,
    params: object = {}
  ): Promise<ResolveEntity<S> | undefined> => {
    const socketConnected = this.rootStore.app?.io?.connected ?? false;
    const isOffline =
      !navigator.onLine ||
      (this.rootStore.connectionStore?.isOffline ?? false) ||
      !socketConnected;

    console.log(
      `[DEBUG] [${this.name}] patch() called - isOffline=${isOffline}, navigator.onLine=${navigator.onLine}, connectionStore.isOffline=${this.rootStore.connectionStore?.isOffline}, socket.connected=${socketConnected}, enableOffline=${this.offlineOptions.enableOffline}`
    );

    if (isOffline && this.offlineOptions.enableOffline) {
      console.log(`[${this.name}] Routing to patchOffline for ID: ${id}`);
      return this.patchOffline(id, data);
    }

    try {
      // Directly call the service
      // if (id === null) {
      //   throw new Error("ID cannot be null for patch");
      // }

      runInAction(() => {
        this.isPatchPending = true;
      });
      let item: ResolveEntity<S> | null = null;

      try {
        item = await this.service.patch(id, data, params);

        runInAction(() => {
          // Use addOrUpdateEvent to prevent duplicates from socket.io "patched" events
          // The socket event may arrive before this promise resolves
          this.addOrUpdateEvent(item as Record<string, unknown>, false);
          this.errorOnPatch = null;
        });
      } catch (error: unknown) {
        runInAction(() => {
          this.errorOnPatch = error as { name: string; message: string };
        });
        throw error;
      } finally {
        runInAction(() => {
          this.isPatchPending = false;
        });
      }

      // Cache the result
      if (this.offlineOptions.enableOffline && this.offlineStorage && item) {
        await this.offlineStorage.setItem(String(id), item);
      }

      return item ?? undefined;
    } catch (error) {
      if (this.offlineOptions.enableOffline) {
        console.warn(`${this.name}: Patching offline`, error);
        return this.patchOffline(id, data);
      }
      throw error;
    }
  };

  private async patchOffline(
    id: number | string | null,
    data: Partial<ResolvePatch<S>>
  ): Promise<ResolveEntity<S>> {
    if (id === null) {
      throw new Error("ID cannot be null for offline patch");
    }
    const existing = this.items.find(
      (item) =>
        String((item as Record<string, unknown>)[this.idField]) === String(id)
    );

    if (!existing) {
      throw new Error(`Item ${id} not found`);
    }

    const updated = {
      ...existing,
      ...data,
      _localUpdatedAt: Date.now(),
      _syncStatus: "pending" as const,
    } as ResolveEntity<S>;

    // Store locally
    if (this.offlineStorage) {
      await this.offlineStorage.setItem(String(id), updated);
    }

    runInAction(() => {
      // Use addOrUpdateEvent for consistency
      this.addOrUpdateEvent(updated as Record<string, unknown>, false);
    });

    // Queue for sync if not a temp item
    if (
      !(existing as OfflineMetadata)._isTemp &&
      this.rootStore.offlineQueueManager
    ) {
      await this.rootStore.offlineQueueManager.enqueue({
        storeName: this.name || "",
        method: "patch",
        entityId: String(id),
        data: toJS(data),
        conflictResolution: this.offlineOptions.conflictStrategy,
      });
    }

    this.pendingSync.add(String(id));

    return updated;
  }

  remove = async (
    id: number | string | null,
    params: object = {}
  ): Promise<ResolveEntity<S> | undefined> => {
    const socketConnected = this.rootStore.app?.io?.connected ?? false;
    const isOffline =
      !navigator.onLine ||
      (this.rootStore.connectionStore?.isOffline ?? false) ||
      !socketConnected;

    if (isOffline && this.offlineOptions.enableOffline) {
      return this.removeOffline(id);
    }

    try {
      // Directly call the service
      // if (id === null) {
      //   throw new Error('ID cannot be null for remove');
      // }

      runInAction(() => {
        this.isRemovePending = true;
      });
      let result: ResolveEntity<S> | null = null;

      try {
        result = await this.service.remove(id, params);

        const index = this.items.findIndex(
          (i) =>
            String((i as Record<string, unknown>)[this.idField]) === String(id)
        );
        if (index >= 0) {
          runInAction(() => {
            this.items.splice(index, 1);
          });
        }

        runInAction(() => {
          this.errorOnRemove = null;
        });
      } catch (error: unknown) {
        runInAction(() => {
          this.errorOnRemove = error as { name: string; message: string };
        });
        throw error;
      } finally {
        runInAction(() => {
          this.isRemovePending = false;
        });
      }

      // Remove from cache
      if (this.offlineOptions.enableOffline && this.offlineStorage) {
        await this.offlineStorage.removeItem(String(id));
      }

      return result ?? undefined;
    } catch (error) {
      if (this.offlineOptions.enableOffline) {
        console.warn(`${this.name}: Removing offline`, error);
        return this.removeOffline(id);
      }
      throw error;
    }
  };

  private async removeOffline(
    id: number | string | null
  ): Promise<ResolveEntity<S>> {
    if (id === null) {
      throw new Error("ID cannot be null for offline remove");
    }
    const item = this.items.find(
      (i) => String((i as Record<string, unknown>)[this.idField]) === String(id)
    );
    if (!item) throw new Error(`Item ${id} not found`);

    // If it's a temp item, just remove it
    if ((item as OfflineMetadata)._isTemp) {
      runInAction(() => {
        const index = this.items.findIndex(
          (i) =>
            String((i as Record<string, unknown>)[this.idField]) === String(id)
        );
        if (index >= 0) {
          this.items.splice(index, 1);
        }
      });

      if (this.offlineStorage) {
        await this.offlineStorage.removeItem(String(id));
      }

      return item;
    }

    // Mark as deleted but keep for sync
    const deletedItem = {
      ...item,
      _deleted: true,
      _localDeletedAt: Date.now(),
      _syncStatus: "pending" as const,
    };

    if (this.offlineStorage) {
      await this.offlineStorage.setItem(String(id), deletedItem);
    }

    runInAction(() => {
      const index = this.items.findIndex(
        (i) =>
          String((i as Record<string, unknown>)[this.idField]) === String(id)
      );
      if (index >= 0) {
        this.items.splice(index, 1);
      }
    });

    // Queue for sync
    if (this.rootStore.offlineQueueManager) {
      await this.rootStore.offlineQueueManager.enqueue({
        storeName: this.name || "",
        method: "remove",
        entityId: String(id),
        conflictResolution: this.offlineOptions.conflictStrategy,
      });
    }

    this.pendingSync.add(String(id));

    return item;
  }

  async syncWithServer() {
    if (!this.offlineOptions.enableOffline || this.syncInProgress) {
      return;
    }

    // Check if online - include socket connection status for accurate detection
    const socketConnected = this.rootStore.app?.io?.connected ?? false;
    const isOnline =
      navigator.onLine &&
      !(this.rootStore.connectionStore?.isOffline ?? false) &&
      socketConnected;

    if (!isOnline) {
      console.log(
        `[${this.name}] Cannot sync - offline (navigator.onLine=${navigator.onLine}, connectionStore.isOffline=${this.rootStore.connectionStore?.isOffline}, socket.connected=${socketConnected})`
      );
      return;
    }

    console.log(`[${this.name}] Starting sync with server...`);

    this.syncInProgress = true;

    try {
      // Process queued operations for this store
      if (this.rootStore.offlineQueueManager) {
        await this.rootStore.offlineQueueManager.processQueue(
          async (op: any) => {
            if (op.storeName !== this.name) return false;

            try {
              switch (op.method) {
                case "create":
                  const created = await this.service.create(op.data);
                  await this.replaceTempItem(op.data, created);
                  return true;

                case "patch":
                  if (op.entityId) {
                    const patched = await this.service.patch(
                      op.entityId,
                      op.data
                    );
                    await this.updateLocalItem(op.entityId, patched);
                    return true;
                  }
                  break;

                case "remove":
                  if (op.entityId) {
                    await this.service.remove(op.entityId);
                    await this.removeLocalItem(op.entityId);
                    return true;
                  }
                  break;
              }
            } catch (error: any) {
              if (error.code === 409) {
                // Conflict - handle it
                return await this.handleConflict(op, error);
              }
              throw error;
            }

            return false;
          },
          async (op, serverData) => {
            // Conflict handler
            const localData = this.items.find(
              (item: any) => item[this.idField] === op.entityId
            );

            if (!localData) return null;

            const resolved = await this.conflictResolver.resolve(
              localData,
              serverData,
              op.conflictResolution ||
                this.offlineOptions.conflictStrategy ||
                "server"
            );

            await this.updateLocalItem(op.entityId!, resolved);
            return resolved;
          }
        );
      }

      // Fetch latest data from server
      await this.find();

      // Update sync time
      this.lastSyncTime = new Date();
      if (this.offlineStorage) {
        await this.offlineStorage.setItem(
          "_lastSync",
          this.lastSyncTime.toISOString()
        );
      }

      // Clear pending sync items
      this.pendingSync.clear();
    } catch (error) {
      console.error(`${this.name}: Sync failed`, error);
      throw error;
    } finally {
      this.syncInProgress = false;
    }
  }

  private async replaceTempItem(tempData: any, serverItem: any) {
    // Find temp item that matches the data
    const tempItem = this.items.find(
      (item: any) => item._isTemp && this.isSameItem(item, tempData)
    );

    if (!tempItem) return;

    const tempId = String((tempItem as any)[this.idField]);
    const serverId = String(serverItem[this.idField]);

    console.log(`[${this.name}] replaceTempItem: ${tempId} -> ${serverId}`);

    // Remove temp item
    if (this.offlineStorage) {
      await this.offlineStorage.removeItem(tempId);
    }

    // Add server item
    if (this.offlineStorage) {
      await this.offlineStorage.setItem(serverId, serverItem);
    }

    runInAction(() => {
      const index = this.items.findIndex(
        (item: any) => String(item[this.idField]) === tempId
      );
      if (index >= 0) {
        this.items[index] = serverItem;
      }
    });

    // Update URL if current route references the temp ID
    this.updateRouteIfTempId(tempId, serverId);
  }

  private updateRouteIfTempId(tempId: string, serverId: string) {
    const routerStore = this.rootStore.routerStore;
    if (!routerStore?.currentRoute) return;

    const params = routerStore.params || {};
    let shouldUpdate = false;
    const newParams: Record<string, string> = { ...params };

    // Check all route params for the temp ID
    for (const [key, value] of Object.entries(params)) {
      if (String(value) === tempId) {
        newParams[key] = serverId;
        shouldUpdate = true;
        console.log(
          `[${this.name}] Updating route param ${key}: ${tempId} -> ${serverId}`
        );
      }
    }

    if (shouldUpdate) {
      // Update the URL without triggering a full navigation
      routerStore.goTo(routerStore.currentRoute.name, newParams, {
        replace: true,
      });
    }
  }

  private async updateLocalItem(id: string, item: any) {
    if (this.offlineStorage) {
      await this.offlineStorage.setItem(id, item);
    }

    runInAction(() => {
      const index = this.items.findIndex((i: any) => i[this.idField] === id);
      if (index >= 0) {
        this.items[index] = item;
      }
    });
  }

  private async removeLocalItem(id: string) {
    if (this.offlineStorage) {
      await this.offlineStorage.removeItem(id);
    }

    runInAction(() => {
      const index = this.items.findIndex((i: any) => i[this.idField] === id);
      if (index >= 0) {
        this.items.splice(index, 1);
      }
    });
  }

  private async handleConflict(operation: any, error: any): Promise<boolean> {
    const serverData = error.serverData;
    const localData = this.items.find(
      (item: any) => item[this.idField] === operation.entityId
    );

    if (!localData || !serverData) return false;

    const resolved = await this.conflictResolver.resolve(
      localData,
      serverData,
      operation.conflictResolution ||
        this.offlineOptions.conflictStrategy ||
        "server"
    );

    await this.updateLocalItem(operation.entityId, resolved);

    return true;
  }

  private isSameItem(item1: any, item2: any): boolean {
    // Compare key fields to determine if it's the same item
    // This is a simple implementation - override for specific needs
    const keys = ["name", "title", "description", "type"];

    for (const key of keys) {
      if (key in item1 && key in item2) {
        if (item1[key] === item2[key]) {
          return true;
        }
      }
    }

    return false;
  }

  private async cacheResults(response: any) {
    if (!this.offlineStorage) return;

    const data = Array.isArray(response)
      ? response
      : response.data || response.result?.data || [];

    for (const item of data) {
      const id = item[this.idField];
      if (id) {
        await this.offlineStorage.setItem(String(id), item);
      }
    }
  }

  async clearOfflineData() {
    if (this.offlineStorage) {
      await this.offlineStorage.clear();
    }

    this.pendingSync.clear();
    this.lastSyncTime = null;
  }

  /**
   * Override dispose to clean up offline-specific resources
   * Note: Can't call super.dispose() since parent uses arrow function property
   */
  dispose = () => {
    this.debugMode &&
      console.log("DISPOSE CALLED FOR OFFLINE SERVICE:", this.name);

    // Clear offline-specific state
    this.pendingSync.clear();
    this.lastSyncTime = null;
    this.syncInProgress = false;

    // Clear the offlineStorage reference
    this.offlineStorage = null;

    // === Parent cleanup logic (from ServiceStore.dispose) ===

    // Clear all reaction disposers to prevent memory leaks
    try {
      this.firsLoadDispose && this.firsLoadDispose();
      this.disposeEntityOptimization && this.disposeEntityOptimization();

      // Dispose batched error reactions
      this.errorReactionsBatchDispose && this.errorReactionsBatchDispose();
      if (this.reactionManager) {
        this.reactionManager.disposeAll();
        this.reactionManager = null;
      }
    } catch (error) {
      console.error(`Error disposing reactions for ${this.name}:`, error);
    }

    // Remove all event listeners properly to prevent memory leaks
    if (this.channelsEnabled && this.service) {
      try {
        this.service.removeAllListeners("created");
        this.service.removeAllListeners("updated");
        this.service.removeAllListeners("patched");
        this.service.removeAllListeners("removed");
        this.service.removeAllListeners("status");
      } catch (error) {
        console.error(
          `Error removing event listeners for ${this.name}:`,
          error
        );
      }
    }

    // Clear storage reference (already null since offlineStorage was cleared above)
    this.storage = null;

    // Clear all observable state to help with garbage collection
    runInAction(() => {
      this.items = [];
      this.pageDataSet = [];
      this.clearErrors();
      this.clearPending();
    });

    this.debugMode &&
      console.log(`OfflineServiceStore ${this.name} disposed successfully`);
  };

  get hasPendingSync() {
    return this.pendingSync.size > 0;
  }

  /**
   * Get all entities in the store, filtering out deleted items
   * @returns Array of entities with full type inference
   */
  get entities(): ResolveEntity<S>[] {
    // Override to filter out deleted items
    return this.items.filter((item) => !(item as OfflineMetadata)._deleted);
  }
}
